import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface RecaptchaContextType {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  executeRecaptcha: (action: string) => Promise<string>;
  setRecaptchaLoaded: (loaded: boolean) => void;
  lastTokenTime: number;
  triggerRecaptchaLoad: () => Promise<boolean>;
}

const RecaptchaContext = createContext<RecaptchaContextType | undefined>(undefined);

interface RecaptchaProviderProps {
  children: ReactNode;
}

export const RecaptchaProvider: React.FC<RecaptchaProviderProps> = ({ children }) => {
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const [isGlobalRecaptchaReady, setIsGlobalRecaptchaReady] = useState(false);
  const [isTestingRecaptcha, setIsTestingRecaptcha] = useState(false);
  const [lastTokenTime, setLastTokenTime] = useState<number>(0);

  // 🚀 SEO OPTIMIZATION: Auto-load reCAPTCHA if URL has video_id
  useEffect(() => {
    const checkUrlForVideoId = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const videoId = urlParams.get('video_id');
      
      if (videoId) {
        console.log('🚀 SEO: URL has video_id, auto-loading reCAPTCHA for payment flow');
        loadRecaptchaScript();
      } else {
        console.log('📱 SEO: No video_id in URL, reCAPTCHA will load on-demand (upload click)');
      }
    };
    
    checkUrlForVideoId();
  }, []);

  // Load reCAPTCHA script only when needed
  const loadRecaptchaScript = () => {
    // Check if script already exists
    if (document.querySelector('script[src*="google.com/recaptcha"]')) {
      console.log('📜 reCAPTCHA script already exists, checking functionality...');
      return Promise.resolve();
    }

    console.log('📜 Loading reCAPTCHA script on demand...');
    
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=6LfcvUgjAAAAAJbHh7QcDTNFh8A4I3vNehz-z6FA';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('🚀 reCAPTCHA script loaded successfully');
        setIsRecaptchaLoaded(true);
        resolve();
      };
      
      script.onerror = () => {
        console.error('❌ Failed to load reCAPTCHA script');
        reject(new Error('Failed to load reCAPTCHA'));
      };

      document.head.appendChild(script);
    });
  };

  // Add method to manually trigger recaptcha loading
  const triggerRecaptchaLoad = async () => {
    if (!isRecaptchaLoaded) {
      console.log('📜 Manually triggering reCAPTCHA load...');
      await loadRecaptchaScript();
    }
    return isRecaptchaLoaded || isGlobalRecaptchaReady;
  };

  // Check if reCAPTCHA is fully functional
  useEffect(() => {
    const checkGlobalRecaptcha = () => {
      const windowWithGrecaptcha = window as any;
      if (windowWithGrecaptcha.grecaptcha && 
          typeof windowWithGrecaptcha.grecaptcha.ready === 'function' &&
          typeof windowWithGrecaptcha.grecaptcha.execute === 'function') {
        
        // Test if reCAPTCHA is actually working
        if (!isTestingRecaptcha) {
          setIsTestingRecaptcha(true);
          
          try {
            windowWithGrecaptcha.grecaptcha.ready(() => {
              // console.log('✅ reCAPTCHA fully functional in context');
              setIsGlobalRecaptchaReady(true);
              setIsTestingRecaptcha(false);
              
              // CSS already handles hiding reCAPTCHA badge
            });
            return true;
          } catch (err) {
            console.log('⏳ reCAPTCHA available but not ready yet in context');
            setIsTestingRecaptcha(false);
            return false;
          }
        }
        return false;
      }
      return false;
    };

    // Check immediately
    if (checkGlobalRecaptcha()) return;

    // Check periodically until fully functional
    const interval = setInterval(() => {
      if (checkGlobalRecaptcha()) {
        clearInterval(interval);
      }
    }, 200); // Balanced interval - not too frequent
    
    return () => clearInterval(interval);
  }, [isTestingRecaptcha]);

  const setRecaptchaLoaded = (loaded: boolean) => {
    setIsRecaptchaLoaded(loaded);
  };

  const value: RecaptchaContextType = {
    isLoaded: isRecaptchaLoaded || isGlobalRecaptchaReady,
    isLoading: false, // Simplified for now
    error: null, // Simplified for now
    lastTokenTime: lastTokenTime,
    executeRecaptcha: async (action: string) => {
      console.log('🔍 RecaptchaContext - executeRecaptcha called with action:', action);
      
      // Load reCAPTCHA script if not already loaded
      try {
        await loadRecaptchaScript();
        
        // Wait for reCAPTCHA to be ready
        await new Promise<void>((resolve) => {
          const checkReady = () => {
            const windowWithGrecaptcha = window as any;
            if (windowWithGrecaptcha.grecaptcha && 
                typeof windowWithGrecaptcha.grecaptcha.ready === 'function') {
              windowWithGrecaptcha.grecaptcha.ready(() => {
                resolve();
              });
            } else {
              setTimeout(checkReady, 100);
            }
          };
          checkReady();
        });
      } catch (error) {
        console.error('❌ Failed to load reCAPTCHA:', error);
        throw new Error('reCAPTCHA not available');
      }
      
      // Ensure reCAPTCHA is fully functional before executing
      const windowWithGrecaptcha = window as any;
      if (!windowWithGrecaptcha.grecaptcha) {
        console.error('❌ RecaptchaContext - grecaptcha not available on window');
        throw new Error('reCAPTCHA not available');
      }

      if (typeof windowWithGrecaptcha.grecaptcha.ready !== 'function' ||
          typeof windowWithGrecaptcha.grecaptcha.execute !== 'function') {
        console.error('❌ RecaptchaContext - grecaptcha methods not available');
        throw new Error('reCAPTCHA methods not available');
      }

      console.log('✅ RecaptchaContext - grecaptcha available, executing...');

      return new Promise((resolve, reject) => {
        windowWithGrecaptcha.grecaptcha.ready(async () => {
          try {
            console.log('🔍 RecaptchaContext - grecaptcha.ready() called, executing...');
            // Always get a fresh token - don't reuse old ones
            const token = await windowWithGrecaptcha.grecaptcha.execute('6LfcvUgjAAAAAJbHh7QcDTNFh8A4I3vNehz-z6FA', { action });
            
            // Update last token time
            setLastTokenTime(Date.now());
            
            console.log('✅ RecaptchaContext - fresh token obtained successfully');
            resolve(token);
          } catch (err) {
            console.error('❌ RecaptchaContext - grecaptcha.execute() failed:', err);
            reject(err);
          }
        });
      });
    },
    setRecaptchaLoaded,
    triggerRecaptchaLoad // Add this method to context
  };

  return (
    <RecaptchaContext.Provider value={value}>
      {children}
    </RecaptchaContext.Provider>
  );
};

export const useRecaptcha = (): RecaptchaContextType => {
  const context = useContext(RecaptchaContext);
  if (context === undefined) {
    throw new Error('useRecaptcha must be used within a RecaptchaProvider');
  }
  return context;
};
